import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";

import { UseUserDetails } from "../../hooks/auth";
import { UseGroupKpis, UseCompanyKpis, UseDraftKpis, useWorkspaceKpiDisable, useWorkspaceKpiUpdate } from "../../hooks/kpi";
import { UseSelectedWorkspace, UseCompanyColors } from "../../hooks/company";
import { FETCH_GROUP_TYPE } from "../../api/group";
import {
  GET_APP_INTEGRATIONS,
  GET_KPI_INTEGRATIONS_BY_DATASET,
  GET_COMPANY_INTEGRATIONS,
} from "../../api/integration";
import {
  GET_ZAPS,
  GET_USER as GET_ZAPIER_USER,
} from "../../api/integrations/zapier";

import { updateWorkspaceKpi } from "../../redux/kpi/actions";

import { Modal, Backdrop, Fade, Box, Grid } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SoftButton from "../SoftButton";
import SoftDialog from "../SoftConfirmationDialog";
import SoftBox from "../SoftBox";
import SoftTypography from "../SoftTypography";

import { Dashboard, MetricSettingsModal } from "../CompanyMetrics";
import { DEFAULT_VISUAL_CONFIG } from "../CompanyMetrics/constants";
import { GET_IMAGE } from "../../helpers/images";
import {
  isKpiGamified,
  KPI_TYPES,
  getAdvancedGameSettingsFromKpi,
  applyAdvancedGameSettingsToKpiPayload,
  applyAnchorGameSettingsToKpiPayload,
  resolveGamePointsRule,
} from "../../constants/kpi";

import CreateCustomKpi from "./metrics/CreateCustomKpi";

import AllocationDialog from "./setup/AllocationDialog";
import SetupDisplayDialog from "./setup/SetupDisplayDialog";
import SetupSelectDialog from "./setup/SetupSelectDialog";
import SoftInput from "../SoftInput";

import { GMB_LOGO_URL } from "../../constants/branding";
import { useNavigate } from "react-router-dom";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import popNotification from "../Modals/popNotification";

const WORKSPACE_COLUMNS_STORAGE_KEY = "gamemybiz_workspace_kpi_columns";

/** Build sparkline placeholder for workspace KPI cards */
function sparklineFromTarget(target = 100, count = 10) {
  const base = typeof target === "number" && !Number.isNaN(target) ? target : 100;
  return Array.from({ length: count }, (_, i) => ({
    timestamp: `${i + 1}`,
    value: Math.round(base * (0.7 + 0.3 * Math.sin(i * 0.5))),
  }));
}

/** Map workspace KPI to Company Metrics metric shape for MetricCard */
function workspaceKpiToMetric(kpi, workspaceId) {
  const kpiId = kpi._id || kpi.id;
  const target = kpi.target ?? kpi.defaultTarget ?? kpi.model?.defaultTarget ?? 100;
  const name = kpi.name || kpi.title || kpi.model?.type || "KPI";
  const isGamifyMetric = isKpiGamified(kpi);
  const advancedSettings = getAdvancedGameSettingsFromKpi(kpi);
  const gameConfig = isGamifyMetric
    ? (() => {
        const { metricAmount, pointsEarned } = resolveGamePointsRule(kpi, advancedSettings);
        // GroupKpi schema does not persist gameConfig, so kpi.gameConfig.type is usually missing
        // after a fetch. The persisted source of truth for progressive vs anchor is kpi.metricType.
        let persistedType;
        if (kpi.metricType === "anchor" || kpi.metricType === "progressive") {
          persistedType = kpi.metricType;
        }
        // Anchor-specific fields are persisted on top-level / optional (see ExploreCustomMetricsTypes
        // create flow + GroupKpi schema). Rehydrate them into gameConfig so MetricSettingsModal
        // re-opens with the saved values instead of defaults.
        let anchorOverrides = {};
        if (persistedType === "anchor") {
          const kindToAnchorUnit = { "%": "percent", $: "currency", "#": "number" };
          const anchorUnit = kindToAnchorUnit[kpi.kind] || "percent";
          const rawTarget = typeof kpi.target === "number" ? kpi.target : Number(kpi.target ?? 0);
          const anchorValue = anchorUnit === "percent" ? Math.round(rawTarget * 100) : rawTarget;
          const opt = kpi.optional || {};
          anchorOverrides = {
            anchorValue,
            anchorUnit,
            benchmarkPoints: typeof opt.benchmarkPoint === "number" ? opt.benchmarkPoint : (opt.benchmarkPoint ?? 1000),
            pointsPerUnitAbove: typeof opt.aboveBenchmark === "number" ? opt.aboveBenchmark : (opt.aboveBenchmark ?? 25),
            pointsPerUnitBelow: typeof opt.belowBenchmark === "number" ? opt.belowBenchmark : (opt.belowBenchmark ?? 25),
          };
        }
        return {
          status: "live",
          dailyTarget: typeof target === "number" ? target : 100,
          ...(kpi.model?.gameConfig && typeof kpi.model.gameConfig === "object" ? kpi.model.gameConfig : {}),
          ...(kpi.gameConfig && typeof kpi.gameConfig === "object" ? kpi.gameConfig : {}),
          ...(persistedType && { type: persistedType }),
          ...anchorOverrides,
          pointsRule: { metricAmount, pointsEarned },
          guideText: typeof kpi.guideText === "string" ? kpi.guideText : "",
          dailyGoalText: typeof kpi.dailyGoalText === "string" ? kpi.dailyGoalText : "",
          monitorMode: advancedSettings.monitorMode,
          scorekeeperOnly: advancedSettings.scorekeeperOnly,
          resetFrequency: advancedSettings.resetFrequency,
          compositeMetric: advancedSettings.compositeMetric,
          valueType: advancedSettings.valueType,
          effectType: advancedSettings.effectType,
          ...(advancedSettings.compositeConfig && { compositeConfig: advancedSettings.compositeConfig }),
        };
      })()
    : undefined;
  // Server stores only appearance type (string) in kpi.appearance
  let appearanceType = null;
  if (typeof kpi.appearance === "string") {
    appearanceType = kpi.appearance;
  } else if (kpi.appearance && typeof kpi.appearance === "object" && typeof kpi.appearance.type === "string") {
    appearanceType = kpi.appearance.type;
  } else if (kpi.visualConfig && typeof kpi.visualConfig === "object" && typeof kpi.visualConfig.type === "string") {
    appearanceType = kpi.visualConfig.type;
  }
  const visualConfig = appearanceType
    ? { ...DEFAULT_VISUAL_CONFIG, type: appearanceType }
    : { ...DEFAULT_VISUAL_CONFIG };
  const typeForCard = appearanceType || visualConfig.type;
  return {
    id: kpiId,
    dashboardId: `ws-${workspaceId}-${kpiId}`,
    workspaceId,
    ownerWorkspaceId: workspaceId,
    name: typeof name === "string" ? name : "Metric",
    // Description used to repeat the target as "Target: NNN" — redundant with
    // the big number rendered on the card. Leave empty so cards stay compact.
    description: "",
    unit: "number",
    currentValue: 0,
    trend: 0,
    sparklineData: sparklineFromTarget(target),
    appId: kpi.integration?.code || kpi.source || "manual",
    deploymentStatus: "active",
    visualConfig,
    appearance: typeof kpi.appearance === "string" ? kpi.appearance : typeForCard,
    _source: "workspaceKpi",
    ...(gameConfig && { gameConfig }),
    ...(kpi.optional && { optional: kpi.optional }),
    ...(kpi.parameters && { parameters: kpi.parameters }),
    ...(kpi.kpiModelId != null && { kpiModelId: kpi.kpiModelId }),
    ...(kpi.requiredInputs && { requiredInputs: kpi.requiredInputs }),
    ...(kpi.kind && { kind: kpi.kind }),
    ...(kpi.isAdminAccess && { isAdminAccess: kpi.isAdminAccess }),
    _id: kpiId,
    isCustom: true,
  };
}

/** Normalize workspace to { id, name, icon } for Dashboard */
function normalizeWorkspace(ws) {
  if (!ws) return { id: "", name: "Workspace", icon: "" };
  return {
    id: ws._id || ws.id,
    name: ws.name || "Workspace",
    icon: ws?.image ? GET_IMAGE(ws.image) : "",
  };
}

const DIALOG_TYPES = {
  ALLOCATION: "allocation",
  DISPLAY_METRIC: "display_metric",
  SETUP: "setup",
  NONE: null
};

// Inline color helpers so the workspace page can hand the same brand-tinted
// CSS variables to MetricCard that /company-metrics uses.
const DEFAULT_BRAND = "#7C3AED";
function hexToRgb(hex) {
  if (!hex) return { r: 124, g: 58, b: 237 };
  let h = hex.replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}
function darken(hex, amt = 0.1) {
  const { r, g, b } = hexToRgb(hex);
  const f = 1 - amt;
  return `rgb(${Math.round(r * f)}, ${Math.round(g * f)}, ${Math.round(b * f)})`;
}
function rgba(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const WorkspaceKpi = () => {
  const { primaryColor: brandPrimary } = UseCompanyColors() || {};
  const themeVars = useMemo(() => {
    const base = brandPrimary || DEFAULT_BRAND;
    return {
      "--gmb-primary": base,
      "--gmb-primary-hover": darken(base, 0.1),
      "--gmb-primary-soft": rgba(base, 0.08),
      "--gmb-primary-tint": rgba(base, 0.14),
      "--gmb-primary-ring": rgba(base, 0.16),
      fontFamily: '"DM Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    };
  }, [brandPrimary]);

  const [kpis, setKpis] = useState([]);
  const [onKpis, setOnKpis] = useState([]);
  const [allKpis, setAllKpis] = useState([]);
  const groupKpis = UseGroupKpis();
  const companyKpis = UseCompanyKpis();
  const workspace = UseSelectedWorkspace();
  const { data: workspaceData } = FETCH_GROUP_TYPE(workspace.typeId);
  const [allApps, setAllApps] = useState([]);
  const [integrationsObj, setIntegrationsObj] = useState(null);
  const [currentAppIntegrations, setCurrentAppIntegrations] = useState({});

  const [newKpi, setNewKpi] = useState(undefined);
  const [currentGroupKpis, setCurrentGroupKpis] = useState(null);
  const [activeDialog, setActiveDialog] = useState(DIALOG_TYPES.NONE);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [connectedApps, setConnecteDApps] = useState([]);
  const [integrationAppsForDashboard, setIntegrationAppsForDashboard] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openCustomModal, setOpenCustomModal] = useState(false);

  const [openMaxMetric, setMaxMetric] = useState(false);
  const { disableWorkspaceKpi } = useWorkspaceKpiDisable();
  const { updateWorkspaceKpi: updateWorkspaceKpiFromHook, updateKpiGuideText, updateKpiDailyGoalText } = useWorkspaceKpiUpdate();
  const [settingsMetric, setSettingsMetric] = useState(null);
  const [settingsTab, setSettingsTab] = useState("appearance");
  const openModal = false; // Request Metric flow removed – modal never shown
  const confirmModal = false;

  const handleCloseMaxMetric = () => {
    setMaxMetric(false);
  };

  const handleMaxMetric = () => {
    setMaxMetric(true);
  };

  const { companyId } = UseUserDetails();

  const MAX_WORKSPACE_KPI = 10;

  const addZapierKpis = async (appsData) => {
    try {
      setIsLoading(true);
      // Don't let GET_ZAPIER_USER failure break the list (e.g. server deleted integration or token expired)
      const [zapTemplatesResponse, zapUserResponse] = await Promise.all([
        GET_ZAPS(),
        GET_ZAPIER_USER().catch(() => ({ data: null })),
      ]);

      const zapTemplates = zapTemplatesResponse.data;
      const zapUser = zapUserResponse?.data ?? null;
      const isZapierConnected = zapUser && !zapUser.tokenExpired;

      zapTemplates.forEach((z) => {
        const idx = appsData.findIndex((app) => {
          return app.name === z.name;
        });
        z.isConnected = !!isZapierConnected;

        const zaps = z.zaps.map((zap) => ({
          ...zap,
          id: z.serviceId,
          // Assign Zapier integration Id for Zapier KPIs that are merged together with other integrated KPIs
          integrationId: z._id,
          // Remove original Game My Biz text from zap since it seems redundant here
          description: zap.description.replace("Game My Biz", ""),
          isZapier: true,
        }));

        if (idx >= 0) {
          appsData[idx].zaps = zaps;
        } else {
          appsData.push({
            ...z,
            zaps,
          });
        }
      });

      setTimeout(() => setIsLoading(false), 500);
      return appsData;
    } catch (err) {
      console.log("zap templates err", err);
      return appsData;
    }
  };

  // Fetch integration apps for MetricCard icons (same as Company Metrics page)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await GET_APP_INTEGRATIONS();
        const apps = Array.isArray(data) ? [...data] : [];
        if (!apps.some((a) => (a.code || a.id) === "manual")) {
          apps.push({
            name: "Game My Biz",
            code: "manual",
            logo: GMB_LOGO_URL,
            isManual: true,
          });
        }
        if (!cancelled) setIntegrationAppsForDashboard(apps);
      } catch {
        if (!cancelled) {
          setIntegrationAppsForDashboard([
            { code: "manual", logo: GMB_LOGO_URL, name: "Game My Biz", isManual: true },
          ]);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (workspaceData) {
      const getIntegrationsData = async () => {
        const [appsResponse, integResponse, companyIntegrationsResponse] =
          await Promise.all([
            GET_APP_INTEGRATIONS(),
            GET_KPI_INTEGRATIONS_BY_DATASET(),
            GET_COMPANY_INTEGRATIONS(),
          ]);

        let appsData = appsResponse.data;
        const integData = integResponse.data;
        const companyIntegrations = companyIntegrationsResponse.data;

        for (let j = 0; j < appsData.length; j += 1) {
          const { code, serviceId } = appsData[j];
          const idx = companyIntegrations.findIndex((app) => {
            return serviceId ? app.serviceId === serviceId : app.code === code;
          });
          if (idx >= 0) {
            appsData[j].isConnected = true;
            appsData[j].paramKeys = { ...companyIntegrations[idx].parameters };
          } else {
            appsData[j].isConnected = false;
          }
        }
        appsData = await addZapierKpis(appsData);

        appsData.push({
          // Add Manual KPIs
          name: "Game My Biz",
          code: "manual",
          logo: GMB_LOGO_URL,
          description: "Custom-built metrics for your business",
          isConnected: true,
          isManual: true,
          models: workspaceData?.data.map((model) => {
            const { _id, ...rest } = model;
            return {
              kpiModelId: _id,
              ...rest,
            };
          }),
        });

        const appsConnected = appsData.filter(
          (item) => item.isConnected === true,
        );

        // Sort apps: connected ones first then sort alphabetically
        const sortedApps = appsData.sort((app1, app2) => {
          // First, sort by isConnected in descending order (true comes first)
          if (app1.isConnected && !app2.isConnected) {
            return -1;
          }
          if (!app1.isConnected && app2.isConnected) {
            return 1;
          }

          // If isConnected values are equal, sort by isZapier in ascending order (false comes first)
          if (!app1.isZapier && app2.isZapier) {
            return -1;
          }
          if (app1.isZapier && !app2.isZapier) {
            return 1;
          }

          // Next, sort by isManual in ascending order (false comes first)
          if (!app1.isManual && app2.isManual) {
            return -1;
          }
          if (app1.isManual && !app2.isManual) {
            return 1;
          }

          // If isConnected and isZapier values are equal, then sort by name in ascending order
          return app1.name.localeCompare(app2.name);
        });

        setConnecteDApps(appsConnected);
        setAllApps(sortedApps);
        setIntegrationsObj(integData);
        setIsLoading(false);
      };

      getIntegrationsData();
    }
  }, [workspaceData]);

  useEffect(() => {
    if (groupKpis) {
      const appIntegrations = {};
      groupKpis
        .filter((kpi) => kpi.integrationId)
        .forEach((kpi) => {
          (appIntegrations[kpi.integrationId] ??= []).push(kpi.sourceId);
        });

      setCurrentAppIntegrations(appIntegrations);

      // Find the newly-added Integrated GroupKpi
      setCurrentGroupKpis(prev => {
        const groupKpiIds = groupKpis.map(kpi => kpi._id);

        if (Array.isArray(prev)) {
          const newGroupKpiId = groupKpiIds.filter(id => !prev.includes(id))?.[0];
          const newGroupKpi = groupKpis.find(kpi => kpi._id === newGroupKpiId);

          // Full newly-saved "display" GroupKpi data from server
          if (newGroupKpi?._id && groupKpis.length > 0) {
            setNewKpi(prevKpi => {
              if (prevKpi?.isNew) {
                setActiveDialog(DIALOG_TYPES.ALLOCATION);
              }
              return { ...prevKpi, ...newGroupKpi }
             });
          }
        }

        return groupKpiIds;
      });
    }
  }, [groupKpis]);

  useEffect(() => {
    // Assign current workspace KPIs (groupKpis) as "active" KPIs; data is fetched on first load and workspace change
    if (kpis.length) {
      const ons = [];
      const all = [];
      kpis.forEach((kpi) => {
        let active = false;
        const idx = groupKpis?.findIndex((k) => k.kpiModelId === kpi._id);
        if (idx >= 0) {
          active = true;
          ons.push({ ...kpi, ...groupKpis[idx] });
          kpi = { ...kpi, ...groupKpis[idx] };
        }
        all.push({
          ...kpi,
          target: kpi.defaultTarget,
          kpiModelId: kpi._id,
          active,
        });
      });
      setOnKpis(ons);
      setAllKpis(all);
    }
  }, [kpis, groupKpis]);

  const handleSwitch = (kpiId, activating = true) => {
    const idx = allKpis.findIndex((k) => k._id === kpiId);
    if (idx >= 0) {
      if (activating) {
        dispatch(
          updateWorkspaceKpi({ ...allKpis[idx], groupId: workspace._id, isCustom: true }),
        );
        setOnKpis([...onKpis, allKpis[idx]]); // Immediately update KPI cards ...
      } else {
        dispatch(disableWorkspaceKpi(kpiId));
        const updatedKpis = [...onKpis]; // Immediately update KPI cards
        updatedKpis.splice(idx, 1); // though this is not too necessary, because the KPI cards will auto-update a bit later
        setOnKpis(updatedKpis); // once redux KPI states updates
      }
    }
  };

  const clearSetupDialogs = (modalDialog) => {
    if (newKpi?.isZapier && newKpi?.integrationMeta) {
      navigate(`/zapier-setup?id=${newKpi?.integrationMeta.id}`);
    } else {
      setActiveDialog(modalDialog ?? DIALOG_TYPES.NONE);
      setNewKpi(null);
    }
  }

  const styleModal = {
    position: 'absolute',
    top: '35%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    // Light frosted-glass shell (Figma spec) — white-ish frost over a soft backdrop.
    background: 'linear-gradient(180deg, rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.60) 100%)',
    borderRadius: '24px',
    border: '1px solid rgba(255,255,255,0.6)',
    boxShadow: '0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7)',
    padding: '1.75rem',
    maxWidth: { mini: "90%", xs: "90%", sm: "600px" },
    width: "100%",
    overflow: "visible",
    backdropFilter: 'blur(18px) saturate(160%)',
    WebkitBackdropFilter: 'blur(18px) saturate(160%)'
  };

  const [showDrafts, setShowDrafts] = useState(false);

  const draftKpis = UseDraftKpis();
  const [openConfirmationPopUp, setOpenConfirmationPopUp] = useState(false);
  const handleConfirmationClose = () => setOpenConfirmationPopUp(false);

  const [kpiData, setkpiData] = useState({});

  const handleDelete = () => {
    handleConfirmationClose();
    dispatch(disableWorkspaceKpi(kpiData._id));
    setTimeout(
      () =>
        popNotification({
          title: "Metric Deleted",
          text: `You've successfully deactivated ${kpiData.title.toUpperCase()} metric.`,
        }),
      1500,
    );
  };

  const [confirmText, setConfirmText] = useState("");
  const [selectMetric, setselectMetric] = useState(false);
  const [columnCount, setColumnCount] = useState(() => {
    const saved = localStorage.getItem(WORKSPACE_COLUMNS_STORAGE_KEY);
    const cols = saved ? parseInt(saved, 10) : 3;
    return Number.isNaN(cols) ? 3 : cols;
  });

  const workspaceId = workspace?._id || workspace?.id || "";
  const gameKpis = useMemo(
    () => (groupKpis || []).filter((k) => !k.isDraft),
    [groupKpis],
  );
  const dataKpis = useMemo(
    () => (draftKpis || []).filter((k) => k.isSaved && k.groupId === workspaceId),
    [draftKpis, workspaceId]
  );
  const workspaceMetricsGame = useMemo(() => gameKpis.map((k) => workspaceKpiToMetric(k, workspaceId)), [gameKpis, workspaceId]);
  const workspaceMetricsData = useMemo(() => dataKpis.map((k) => workspaceKpiToMetric(k, workspaceId)), [dataKpis, workspaceId]);
  /** Single unified list: game metrics first, then data metrics (no tabs) */
  const activeMetrics = useMemo(
    () => [...workspaceMetricsGame, ...workspaceMetricsData],
    [workspaceMetricsGame, workspaceMetricsData]
  );
  const allWorkspacesList = useMemo(() => (workspace ? [normalizeWorkspace(workspace)] : []), [workspace]);

  const handleUpdateColumns = (cols) => {
    setColumnCount(cols);
    localStorage.setItem(WORKSPACE_COLUMNS_STORAGE_KEY, cols.toString());
  };

  const handleRemoveMetric = (dashboardId) => {
    const metric = activeMetrics.find((m) => m.dashboardId === dashboardId);
    if (metric) {
      disableWorkspaceKpi(metric.id);
      popNotification({
        title: "Metric Deleted",
        text: `You've successfully deactivated ${(metric.name || "metric").toUpperCase()} metric.`,
      });
    }
  };

  const handleEditMetric = (metric, tab) => {
    setSettingsMetric(metric);
    setSettingsTab(tab || "appearance");
  };

  /**
   * Persist appearance/gamification edits from MetricSettingsModal to the server.
   * Mirrors the working pattern in pages/CompanyMetrics/index.jsx:handleSaveMetricFromSettings:
   * - Resolve the raw KPI from groupKpis so integrationId/sourceId/kpiModelId/optional
   *   are preserved on the payload (otherwise the server $unsets them on update).
   * - Send appearance as { type, chartGranularity } and gameConfig untouched.
   * - Sync top-level fields (name, target, pointScale, pointValue, metricType) from
   *   gameConfig so manual edits to title and gameplay numbers persist.
   * - Surface composite metrics via top-level dataSourceOne/calculationMethod/dataSourceTwo.
   * - Do NOT call disableWorkspaceKpi here — that flips disabled:true on the KPI
   *   (treated as account disable for integrated metrics).
   */
  const handleUpdateMetricFromSettings = async (metricIdOrDashboardId, visual, game, shouldClose) => {
    if (!settingsMetric || !workspaceId) {
      if (shouldClose) setSettingsMetric(null);
      return;
    }
    const metricId = settingsMetric.id || settingsMetric._id || metricIdOrDashboardId;
    const rawKpi = (groupKpis || []).find((k) => (k._id || k.id) === metricId);
    if (!rawKpi) {
      popNotification({
        title: "Update failed",
        text: "Could not find KPI to update on the server.",
      });
      if (shouldClose) setSettingsMetric(null);
      return;
    }
    // Preserve the metric's own true home groupId (its rawKpi.groupId) rather
    // than always writing the CURRENT workspace — for a metric merely linked
    // onto this board (see GroupKpi.linkedGroupIds), sending workspaceId here
    // would silently move its home away from wherever it actually lives
    // (e.g. Company Metrics' companyId) every time it's saved from here.
    const homeGroupId = rawKpi.groupId?._id || rawKpi.groupId || workspaceId;
    const kpiPayload = {
      ...rawKpi,
      groupId: homeGroupId,
      isCustom: true,
      appearance: {
        type: typeof visual?.type === "string" ? visual.type : DEFAULT_VISUAL_CONFIG.type,
        chartGranularity: visual?.chartGranularity === "week" ? "week" : "day",
      },
      ...(game !== undefined && { gameConfig: game }),
      ...(game && game.status === "live" ? { isDraft: false, isSaved: false } : {}),
    };
    if (game && typeof game === "object") {
      if (typeof game.gameName === "string" && game.gameName.trim() !== "") {
        kpiPayload.name = game.gameName.trim();
      }
      if (game.dailyTarget != null && game.dailyTarget !== "") {
        kpiPayload.target = Number(game.dailyTarget);
      }
      const pr = game.pointsRule;
      if (pr && typeof pr === "object") {
        if (pr.metricAmount != null && pr.metricAmount !== "") kpiPayload.pointScale = Number(pr.metricAmount);
        if (!game.monitorMode && pr.pointsEarned != null && pr.pointsEarned !== "") {
          kpiPayload.pointValue = Number(pr.pointsEarned);
        }
      }
      if (game.type != null && KPI_TYPES.includes(game.type)) {
        kpiPayload.metricType = game.type;
      }
      if (game.type === "anchor") {
        applyAnchorGameSettingsToKpiPayload(kpiPayload, game);
      }
    }
    applyAdvancedGameSettingsToKpiPayload(kpiPayload, rawKpi, game);
    try {
      await updateWorkspaceKpiFromHook(workspaceId, kpiPayload);
      popNotification({
        title: "Appearance saved",
        text: "KPI appearance has been updated successfully.",
      });
      if (shouldClose) setSettingsMetric(null);
    } catch (err) {
      popNotification({
        title: "Update failed",
        text: err?.message || "Could not save appearance settings.",
      });
      throw err;
    }
  };

  const handleSaveGuideText = useCallback(
    async (metric, guideText) => {
      const kpiId = metric?.id || metric?._id;
      if (!kpiId) {
        throw new Error("Could not find KPI to update on the server.");
      }
      const trimmed = typeof guideText === "string" ? guideText.trim() : "";
      await updateKpiGuideText(kpiId, trimmed);
    },
    [updateKpiGuideText],
  );

  const handleSaveDailyGoalText = useCallback(
    async (metric, dailyGoalText) => {
      const kpiId = metric?.id || metric?._id;
      if (!kpiId) {
        throw new Error("Could not find KPI to update on the server.");
      }
      const trimmed = typeof dailyGoalText === "string" ? dailyGoalText.trim() : "";
      await updateKpiDailyGoalText(kpiId, trimmed);
    },
    [updateKpiDailyGoalText],
  );

  const handleCreateIntegrated = () => {
    if (groupKpis && groupKpis.filter((k) => !k.isDraft).length >= MAX_WORKSPACE_KPI) {
      handleMaxMetric();
      return;
    }
    navigate("/company-metrics?create=integrated");
  };

  const handleCreateManual = () => {
    navigate("/company-metrics?create=manual");
  };

  return (
    <div style={themeVars}>
      {activeDialog === DIALOG_TYPES.ALLOCATION && (
        <Box className="flex justify-end mb-4">
          <AllocationDialog
            isOpen
            handleCancel={() => clearSetupDialogs(DIALOG_TYPES.DISPLAY_METRIC)}
            handleConfirm={() => setActiveDialog(DIALOG_TYPES.SETUP)}
          />
        </Box>
      )}
      <CreateCustomKpi
        openCustomKpi={openCustomModal}
        handleCloseCustomKpi={() => setOpenCustomModal(false)}
      />
      <Box className="flex justify-between">
              <Modal
                open={false}
                onClose={() => {}}
                BackdropComponent={Backdrop}
                BackdropProps={{
                  timeout: 500,
                }}
              >
                <Fade in={false}>
                  <SoftBox sx={styleModal}>
                    <SoftBox alignItems="center" sx={{padding: '30px'}}>
                      <svg width="71" height="71" viewBox="0 0 71 71" fill="none" xmlns="http://www.w3.org/2000/svg" style={{margin: "0 auto"}}>
                        <circle cx="35.5" cy="35.5" r="35.2809" fill="url(#paint0_linear_149_11933)" fillOpacity="0.2" stroke="url(#paint1_linear_149_11933)" strokeWidth="0.438272"/>
                        <circle cx="35.5001" cy="35.5001" r="28.7068" fill="url(#paint2_linear_149_11933)" fillOpacity="0.5" stroke="url(#paint3_linear_149_11933)" strokeWidth="0.438272"/>
                        <path fillRule="evenodd" clipRule="evenodd" d="M47.7467 25.9868C48.1213 26.3618 48.3316 26.8701 48.3316 27.4001C48.3316 27.9301 48.1213 28.4384 47.7467 28.8134L31.4881 45.0721C31.3004 45.2596 31.0772 45.4079 30.8317 45.5083C30.5861 45.6088 30.323 45.6594 30.0577 45.6571C29.7924 45.6549 29.5302 45.5999 29.2863 45.4953C29.0425 45.3908 28.8219 45.2387 28.6374 45.0481L22.2294 38.4241C21.864 38.0422 21.6645 37.5313 21.6747 37.0028C21.6848 36.4744 21.9038 35.9714 22.2836 35.6039C22.6635 35.2364 23.1734 35.0342 23.7019 35.0415C24.2304 35.0487 24.7345 35.2649 25.1041 35.6428L30.0987 40.8054L44.9201 25.9868C45.2951 25.6122 45.8034 25.4019 46.3334 25.4019C46.8634 25.4019 47.3717 25.6122 47.7467 25.9868Z" fill="#299E47"/>
                        <defs>
                        <linearGradient id="paint0_linear_149_11933" x1="35.5" y1="3.28704" x2="35.7772" y2="104.192" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#299E47" stopOpacity="0"/>
                        <stop offset="0.300199" stopColor="#299E47" stopOpacity="0.06"/>
                        <stop offset="1" stopColor="#299E47"/>
                        </linearGradient>
                        <linearGradient id="paint1_linear_149_11933" x1="35.5" y1="0" x2="35.5" y2="87.2161" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#98A2B3" stopOpacity="0.03"/>
                        <stop offset="1" stopColor="#41464D" stopOpacity="0.16"/>
                        </linearGradient>
                        <linearGradient id="paint2_linear_149_11933" x1="35.5001" y1="6.57422" x2="36.6752" y2="98.3835" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#299E47" stopOpacity="0.5"/>
                        <stop offset="0.356773" stopColor="#299E47" stopOpacity="0.2"/>
                        <stop offset="1" stopColor="#299E47" stopOpacity="0.7"/>
                        </linearGradient>
                        <linearGradient id="paint3_linear_149_11933" x1="35.5001" y1="6.57422" x2="35.5001" y2="64.4261" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#475467" stopOpacity="0.44"/>
                        <stop offset="1" stopColor="#8DA7CD" stopOpacity="0.06"/>
                        </linearGradient>
                        </defs>
                      </svg>
                      <SoftTypography sx={{textFillColor: "#0F172A", fontSize: '28px', textAlign: 'center', marginTop: '1rem'}} variant="h6" component="h2">
                        Metric Request Received
                      </SoftTypography>
                      <SoftTypography sx={{textFillColor: "#475467", textAlign: 'center', fontSize: '16px', fontWeight: '400', marginTop: '1.5rem'}} variant="p" component="p">
                        Your request will be reviewed promptly, and we'll have the metric ready for you in as soon as possible!
                      </SoftTypography>
                      <SoftBox alignItems="center" sx={{textAlign: 'center'}}>
                        <SoftButton sx={{width: '40%', marginTop: '5rem'}} onClick={() => {}}>
                          Okay 
                        </SoftButton>
                      </SoftBox>
                    </SoftBox>
                  </SoftBox>
                </Fade>
              </Modal>
              <Modal
                open={false}
                onClose={() => {}}
                BackdropComponent={Backdrop}
                BackdropProps={{
                  timeout: 500,
                }}
              >
                <Fade in={false}>
                  <SoftBox sx={styleModal}>
                    <SoftBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <SoftTypography sx={{textFillColor: "#0F172A"}} variant="h6" component="h2">
                        Request Metric Creation
                      </SoftTypography>
                      <SoftButton
                        variant="outlined"
                        color="white"
                        onClick={() => {}}
                        sx={{
                          "& svg": { fontSize: "1.25rem !important" },
                          borderRadius: "50%",
                          minWidth: "max-content",
                          minHeight: "max-content",
                          padding: "0.25rem",
                        }}
                      >
                        <span aria-hidden>×</span>
                      </SoftButton>
                    </SoftBox>
                    <hr style={{marginTop: '1rem', marginBottom: '1rem', border: 'none', borderTop: '1px solid rgba(15,23,42,0.10)'}}/>
                    <SoftTypography sx={{textFillColor: "#475467", textAlign: 'center', fontSize: '14px', fontWeight: '400'}} variant="p" component="p">
                      Not sure how to set up the right metric for your team? We're here to help!
                    </SoftTypography>
                    <SoftTypography sx={{textFillColor: "#475467", textAlign: 'center', fontSize: '14px', fontWeight: '400'}} variant="p" component="p">
                      Simply fill out the details below, and our expert team will create the perfect metric
                    </SoftTypography>
                    <SoftBox sx={{marginTop: '2rem'}}>
                      <form>
                        <SoftTypography sx={{textFillColor: "#475467", fontSize: '14.5px', fontWeight: '400'}} variant="p" component="p">
                          Name of desired metric
                        </SoftTypography>
                        <SoftInput
                          name="title"
                          type="text"
                          placeholder="Title of the metric"
                          sx={{
                            border: "1px solid #E4E7EC",
                            backgroundColor: "#F6F7F9 !important",
                            color: "#1d2939 !important",
                            "& .MuiInputBase-input::placeholder": {
                              color: "#98A2B3 !important",
                              opacity: "1",
                            },
                            marginBottom: '1rem'
                          }}
                          size="large"
                        />
                        <SoftTypography sx={{textFillColor: "#475467", fontSize: '14.5px', fontWeight: '400', marginTop: '1rem'}} variant="p" component="p">
                          Any integrations in mind?
                        </SoftTypography>
                        <SoftInput
                          name="integration"
                          type="text"
                          placeholder="Please specify"
                          sx={{
                            border: "1px solid #E4E7EC",
                            backgroundColor: "#F6F7F9 !important",
                            color: "#1d2939 !important",
                            "& .MuiInputBase-input::placeholder": {
                              color: "#98A2B3 !important",
                              opacity: "1",
                            },
                            marginBottom: '1rem'
                          }}
                          size="large"
                        />
                        <SoftTypography sx={{textFillColor: "#475467", fontSize: '14.5px', fontWeight: '400'}} variant="p" component="p">
                          Details
                        </SoftTypography>
                        <SoftInput
                          name="details"
                          type="textarea"
                          placeholder="Any specific requirements or desired outcome"
                          sx={{
                            border: "1px solid #E4E7EC",
                            backgroundColor: "#F6F7F9 !important",
                            color: "#1d2939 !important",
                            "& .MuiInputBase-input::placeholder": {
                              color: "#98A2B3 !important",
                              opacity: "1",
                            },
                            marginBottom: '1rem'
                          }}
                          size="large"
                        />
                        <SoftBox display="flex" justifyContent="space-between" alignItems="center" sx={{textAlign: 'center'}}>
                          <SoftButton sx={{width: '40%', marginTop: '1.5rem', background: '#F9FAFB!important', color: '#667085!important'}} onClick={() => {}}>
                            Cancel
                          </SoftButton>
                          <SoftButton sx={{width: '40%', marginTop: '1.5rem'}} onClick={() => {}}>
                            Submit
                          </SoftButton>
                        </SoftBox>
                      </form>
                    </SoftBox>
                  </SoftBox>
                </Fade>
              </Modal>
              <Modal
                open={openMaxMetric}
                onClose={handleCloseMaxMetric}
                BackdropComponent={Backdrop}
                BackdropProps={{
                  timeout: 500,
                }}
              >
                <Fade in={openMaxMetric}>
                  <SoftBox sx={styleModal}>
                    <SoftBox display="flex" justifyContent="center" alignItems="center">
                      <svg width="71" height="71" viewBox="0 0 71 71" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="35.5" cy="35.5" r="35.2809" fill="url(#paint0_linear_175_5395)" fillOpacity="0.2" stroke="url(#paint1_linear_175_5395)" strokeWidth="0.438272"/>
                        <circle cx="35.5001" cy="35.5001" r="28.7068" fill="url(#paint2_linear_175_5395)" fillOpacity="0.5" stroke="url(#paint3_linear_175_5395)" strokeWidth="0.438272"/>
                        <path d="M36.0001 28.333C36.2653 28.333 36.5197 28.4384 36.7072 28.6259C36.8947 28.8134 37.0001 29.0678 37.0001 29.333V35.333C37.0001 35.5982 36.8947 35.8526 36.7072 36.0401C36.5197 36.2276 36.2653 36.333 36.0001 36.333C35.7349 36.333 35.4805 36.2276 35.293 36.0401C35.1054 35.8526 35.0001 35.5982 35.0001 35.333V29.333C35.0001 29.0678 35.1054 28.8134 35.293 28.6259C35.4805 28.4384 35.7349 28.333 36.0001 28.333ZM36.0001 41.6663C36.3537 41.6663 36.6928 41.5259 36.9429 41.2758C37.1929 41.0258 37.3334 40.6866 37.3334 40.333C37.3334 39.9794 37.1929 39.6402 36.9429 39.3902C36.6928 39.1401 36.3537 38.9997 36.0001 38.9997C35.6465 38.9997 35.3073 39.1401 35.0573 39.3902C34.8072 39.6402 34.6667 39.9794 34.6667 40.333C34.6667 40.6866 34.8072 41.0258 35.0573 41.2758C35.3073 41.5259 35.6465 41.6663 36.0001 41.6663Z" fill="#981B14"/>
                        <path d="M29.7707 20.9603C29.8635 20.8674 29.9736 20.7936 30.0949 20.7433C30.2161 20.693 30.3461 20.667 30.4774 20.667H41.5227C41.7881 20.667 42.0414 20.7723 42.2294 20.9603L50.0401 28.771C50.2281 28.9577 50.3334 29.211 50.3334 29.4777V40.523C50.3334 40.6543 50.3074 40.7843 50.2571 40.9055C50.2068 41.0268 50.133 41.1369 50.0401 41.2297L42.2294 49.0403C42.1367 49.1333 42.0265 49.207 41.9053 49.2574C41.784 49.3077 41.654 49.3336 41.5227 49.3337H30.4774C30.3461 49.3336 30.2161 49.3077 30.0949 49.2574C29.9736 49.207 29.8635 49.1333 29.7707 49.0403L21.9601 41.2297C21.8671 41.1369 21.7934 41.0268 21.7431 40.9055C21.6927 40.7843 21.6668 40.6543 21.6667 40.523V29.4777C21.6667 29.2123 21.7721 28.959 21.9601 28.771L29.7707 20.9603ZM30.8907 22.667L23.6667 29.8923V40.1083L30.8921 47.3337H41.1081L48.3334 40.1097V29.891L41.1094 22.667H30.8907Z" fill="#981B14"/>
                        <defs>
                        <linearGradient id="paint0_linear_175_5395" x1="35.5" y1="3.28704" x2="35.7772" y2="104.192" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#9E2929" stopOpacity="0"/>
                        <stop offset="0.300199" stopColor="#9E2929" stopOpacity="0.06"/>
                        <stop offset="1" stopColor="#9E2929"/>
                        </linearGradient>
                        <linearGradient id="paint1_linear_175_5395" x1="35.5" y1="0" x2="35.5" y2="87.2161" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#98A2B3" stopOpacity="0.03"/>
                        <stop offset="1" stopColor="#41464D" stopOpacity="0.16"/>
                        </linearGradient>
                        <linearGradient id="paint2_linear_175_5395" x1="35.5001" y1="6.57422" x2="36.6752" y2="98.3835" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#9E2929" stopOpacity="0"/>
                        <stop offset="0.356773" stopColor="#9E2929" stopOpacity="0.2"/>
                        <stop offset="1" stopColor="#9E2929" stopOpacity="0.7"/>
                        </linearGradient>
                        <linearGradient id="paint3_linear_175_5395" x1="35.5001" y1="6.57422" x2="35.5001" y2="64.4261" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#475467" stopOpacity="0.44"/>
                        <stop offset="1" stopColor="#8DA7CD" stopOpacity="0.06"/>
                        </linearGradient>
                        </defs>
                      </svg>
                    </SoftBox>
                    <SoftTypography sx={{textFillColor: "#0F172A", textAlign: 'center', fontSize: '28px', fontWeight: '500', marginTop: '1rem', marginBottom: '1rem'}} variant="p" component="p">
                      Maximum Metrics Reached
                    </SoftTypography>
                    <SoftTypography sx={{textFillColor: "#475467", textAlign: 'center', fontSize: '18px', fontWeight: '400', marginBottom: '3rem'}} variant="p" component="p">
                      You have already created the maximum number of<br/> metrics allowed in this workspace. To create more,<br/> you'll need to delete an existing metric. 
                    </SoftTypography>
                    <SoftBox display="flex" justifyContent="center" alignItems="center" sx={{textAlign: 'center'}}>
                      <SoftButton sx={{width: '40%', marginTop: '1.5rem', background: '#F9FAFB!important', color: '#667085!important'}} onClick={handleCloseMaxMetric}>
                        Okay
                      </SoftButton>
                    </SoftBox>
                  </SoftBox>
                </Fade>
              </Modal>
            </Box>
        <Box>
          <Box
            sx={{
              marginTop: { mini: "1rem", lg: "0rem" },
              marginBottom: "2rem",
              width: "100%",
            }}
          >
            <Dashboard
              metrics={activeMetrics}
              allMetrics={activeMetrics}
              columnCount={columnCount}
              onUpdateColumns={handleUpdateColumns}
              onAddIntegrated={handleCreateIntegrated}
              onAddManual={handleCreateManual}
              onRemoveMetric={handleRemoveMetric}
              onEditMetric={handleEditMetric}
              onTriggerAssign={() => {}}
              onTriggerGamifyForWorkspace={() => {}}
              allWorkspaces={allWorkspacesList}
              integrationApps={integrationAppsForDashboard.length > 0 ? integrationAppsForDashboard : (allApps || [])}
              isGlobalView={false}
              userRole="admin"
              kpiLoading={false}
              rawKpis={groupKpis || []}
              onSaveGuideText={handleSaveGuideText}
              onSaveDailyGoalText={handleSaveDailyGoalText}
            />
          </Box>
          <Box>
          <Box
              sx={{
                marginTop: "3rem",
                marginBottom: "2rem",
                width: "100%",
              }}
            >
              <Grid container spacing={3}>
                <Grid item mb={3}>
                  {/* <SoftBox sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <SoftTypography
                      className="cursor-pointer"
                      sx={{
                        fontFamily: 'DM Sans',
                        fontStyle: 'normal',
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '18px',
                        color: '#667085',
                      }}
                      onClick={() => setShowDrafts(!showDrafts)} 
                    >
                      {showDrafts ? "Hide" : "Show"} Draft Metrics
                      <span 
                        style={{
                          width: "15px",
                          height: "15px",
                          background: "linear-gradient(273.63deg, #7928CA 0%, #FF0080 100%)",
                          boxShadow: "0px 14px 16.8px -2.8px rgba(16, 24, 40, 0.08), 0px 5.6px 5.6px -2.8px rgba(16, 24, 40, 0.03)",
                          borderRadius: "50%",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontFamily: "'DM Sans', sans-serif",
                          fontStyle: "normal",
                          fontWeight: 500,
                          fontSize: "9.8px",
                          lineHeight: "13px",
                          color: "#FFFFFF",
                          marginLeft: "0.25rem",
                        }}>
                        {draftKpis?.filter(kpi => !kpi.isSaved).length}
                      </span>
                    </SoftTypography>
                    {showDrafts ? (
                      <KeyboardArrowUpIcon style={{ color: "#051923", width: "20px", height: "20px" }} />
                    ) : (
                      <KeyboardArrowDownIcon style={{ color: "#051923", width: "20px", height: "20px" }} />
                    )}
                  </SoftBox> */}
                </Grid>
              </Grid>
              {showDrafts && draftKpis && draftKpis.length > 0 && (
                <SoftBox>
                  <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "#fff", borderRadius: "0px", overflow: "hidden" }}>
                    <thead>
                      <tr style={{ background: "#F2F4F7" }}>
                        <th style={{ padding: "16px", width: "33.33%", textAlign: "left", fontFamily: "'DM Sans', sans-serif", fontStyle: "normal", fontWeight: 500, fontSize: "14px", lineHeight: "18px", color: "#000000" }}>KPI Name</th>
                        <th style={{ padding: "16px", width: "33.33%", textAlign: "left", fontFamily: "'DM Sans', sans-serif", fontStyle: "normal", fontWeight: 500, fontSize: "14px", lineHeight: "18px", color: "#000000" }}>Created At</th>
                        <th style={{ padding: "16px", width: "33.33%", textAlign: "left", fontFamily: "'DM Sans', sans-serif", fontStyle: "normal", fontWeight: 500, fontSize: "14px", lineHeight: "18px", color: "#000000" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {draftKpis
                        .filter(kpi => !kpi.isSaved)
                        .sort((a, b) => {
                          // sort by date descending (most recent first)
                          const aDate = a.date ? new Date(a.date).getTime() : 0;
                          const bDate = b.date ? new Date(b.date).getTime() : 0;
                          return bDate - aDate;
                        })
                        .map((kpi) => (
                          <tr key={kpi._id} style={{ borderBottom: "1px solid #ececec" }}>
                            <td style={{ padding: "16px", fontFamily: "'DM Sans', sans-serif", fontStyle: "normal", fontWeight: 500, fontSize: "14px", lineHeight: "18px", color: "#000000" }}>
                              {kpi.title}
                            </td>
                            <td style={{ padding: "16px", fontFamily: "'DM Sans', sans-serif", fontStyle: "normal", fontWeight: 500, fontSize: "14px", lineHeight: "18px", color: "#000000" }}>
                              {kpi.date ? new Date(kpi.date).toLocaleDateString() : "-"}
                            </td>
                            <td style={{ padding: "16px" }}>
                              <SoftBox sx={{ display: "flex", gap: 1 }}>
                                <SoftButton
                                  variant="contained"
                                  color="info"
                                  size="small"
                                  onClick={() => navigate(`/workspace-kpi/custom-metric-types?kpiid=${kpi._id}`)}
                                  sx={{
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontStyle: "normal",
                                    fontWeight: 500,
                                    fontSize: "14px",
                                    lineHeight: "18px",
                                    color: "#000000",
                                    background: "#D4EAD7",
                                    padding: "0rem 1rem",
                                    boxShadow: "none!important",
                                    "&:hover": {
                                      background: "#D4EAD7",
                                    },
                                  }}
                                >
                                  Continue
                                </SoftButton>
                                <SoftButton
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  onClick={() => { setOpenConfirmationPopUp(true); setkpiData(kpi); }}
                                  sx={{
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontStyle: "normal",
                                    fontWeight: 500,
                                    fontSize: "14px",
                                    lineHeight: "18px",
                                    color: "#000000",
                                    background: "#F2F4F7",
                                    border: "none!important",
                                    padding: "0rem 0rem",
                                    boxShadow: "none!important",
                                    "&:hover": {
                                      background: "#F2F4F7",
                                    },
                                  }}
                                >
                                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5.25 15.75C4.8375 15.75 4.4845 15.6033 4.191 15.3097C3.8975 15.0162 3.7505 14.663 3.75 14.25V4.5H3V3H6.75V2.25H11.25V3H15V4.5H14.25V14.25C14.25 14.6625 14.1033 15.0157 13.8097 15.3097C13.5162 15.6038 13.163 15.7505 12.75 15.75H5.25ZM12.75 4.5H5.25V14.25H12.75V4.5ZM6.75 12.75H8.25V6H6.75V12.75ZM9.75 12.75H11.25V6H9.75V12.75Z" fill="black"/>
                                  </svg>
                                </SoftButton>
                              </SoftBox>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </SoftBox>
              )}
            </Box>
          </Box>
        </Box>
      {openConfirmationPopUp && (
        <SoftDialog
          open={openConfirmationPopUp}
          onClose={handleConfirmationClose}
          title={`Delete ${kpiData.title} Metric?`}
          type="error"
          icon={<WarningAmberIcon fontSize="large" sx={{ color: "#981B14" }} />}
          description={
            <SoftBox display="flex" flexDirection="column" alignItems="center" mt="25px" mb="35px">
              <SoftTypography
                variant="p"
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#FFF",
                  textAlign: "center",
                  marginBottom: kpiData.title === "Yard Sign" ? "20px" : "0",
                }}
              >
                {kpiData.title === "Yard Sign" 
                  ? "If you proceed with deleting this metric, it may affect the badge connected to it."
                  : "This action cannot be undone and all associated data will be lost. Proceed with caution."
                }
              </SoftTypography>
              {kpiData.title === "Yard Sign" && (
                <>
                <SoftTypography
                variant="p"
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#FFF",
                  textAlign: "center",
                }}
              >
                Type 'confirm' to proceed
              </SoftTypography>
                <SoftInput
                  type="text"
                  placeholder="Type 'confirm' to proceed"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  sx={{
                    width: "100%",
                    borderRadius: "4px",
                    border: "1px solid rgba(233, 236, 239, 0.20)",
                  }}
                />
                </>
              )}
            </SoftBox>
          }
          footer={
            <SoftBox display="flex" gap="10px" justifyContent="center">
              <SoftButton
                size="small"
                variant="contained"
                color="outlined"
                onClick={handleConfirmationClose}
                sx={{
                  textTransform: "none",
                  borderRadius: "4px",
                  border: "1px solid rgba(233, 236, 239, 0.20)",
                  boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.05)",
                  background: "rgba(255, 255, 255, 0.10)",
                  color: "#CED4DA",
                  "&:hover": {
                    background: "rgba(255, 255, 255, 0.10)",
                  },
                }}
              >
                Cancel
              </SoftButton>
              <SoftButton
                size="small"
                color="white"
                onClick={handleDelete}
                sx={{
                  width: "min-content",
                  borderRadius: "4px",
                  textTransform: "none",
                  color: "#344054",
                  opacity: 1,
                }}
              >
                Proceed
              </SoftButton>
            </SoftBox>
          }
        />
      )}

      {settingsMetric && (
        <MetricSettingsModal
          metric={settingsMetric}
          allMetrics={activeMetrics}
          allWorkspaces={allWorkspacesList}
          isGlobalView={false}
          activeWorkspace={normalizeWorkspace(workspace)}
          initialTab={settingsTab}
          onClose={() => setSettingsMetric(null)}
          onReopenWorkspaceSelector={() => {}}
          onSave={async (metricId, visual, game, shouldClose = true) => {
            await handleUpdateMetricFromSettings(metricId, visual, game, shouldClose);
          }}
        />
      )}

      {(activeDialog === DIALOG_TYPES.DISPLAY_METRIC) &&
        <SetupDisplayDialog
          open
          handleReset={clearSetupDialogs}
        />
      }

      {(activeDialog === DIALOG_TYPES.SETUP) &&
        <SetupSelectDialog
          open
          metric={newKpi}
          handleReset={clearSetupDialogs}
        />
      }
    </div>
  );
};

export default WorkspaceKpi;
